import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import './Header.css';

const NAV = [
  {
    label: 'About',
    path: '/about',
    items: [
      { label: 'Our Team', path: '/about#team' },
      { label: 'Our Space', path: '/about#space' },
      { label: 'Gallery', path: '/gallery' },
      { label: 'Awards & Certificates', path: '/awards' },
    ],
  },
  {
    label: 'Services',
    path: '/services',
  },
  {
    label: 'Consultation',
    path: '/consultation',
  },
  {
    label: 'Events',
    path: '/events',
    items: [
      { label: 'Upcoming Events', path: '/events' },
      { label: 'Past Events', path: '/events#past' },
    ],
  },
  {
    label: 'Resources',
    path: '/blogs',
    items: [
      { label: 'Blogs', path: '/blogs' },
      { label: 'Books', path: '/books' },
    ],
  },
  {
    label: 'Testimonials',
    path: '/testimonials',
    items: [
      { label: 'Client Testimonials', path: '/testimonials#client' },
      { label: 'Trainee Testimonials', path: '/testimonials#trainee' },
      { label: 'Reviews', path: '/testimonials#reviews' },
    ],
  },
  {
    label: 'Contact',
    path: '/contact',
    items: [
      { label: 'WhatsApp', path: 'https://wa.me/919140395816', external: true },
      { label: 'Location', path: '/contact#location' },
    ],
  },
];

const Chevron = () => (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMobileDropdown, setOpenMobileDropdown] = useState(null);
  const [studentMe, setStudentMe] = useState(null);
  const [studentMenuOpen, setStudentMenuOpen] = useState(false);
  const studentMenuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Close the student dropdown on click-outside or Escape. mouseLeave on the
  // wrapper closed it too aggressively — the 8 px gap between the toggle and
  // the menu fell outside the wrapper's bounding box and slammed it shut on
  // the way down.
  useEffect(() => {
    if (!studentMenuOpen) return;
    const handleClick = (e) => {
      if (studentMenuRef.current && !studentMenuRef.current.contains(e.target)) {
        setStudentMenuOpen(false);
      }
    };
    const handleKey = (e) => { if (e.key === 'Escape') setStudentMenuOpen(false); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [studentMenuOpen]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenMobileDropdown(null);
    setStudentMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Lock page scroll while the off-canvas mobile menu is open.
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isMobileMenuOpen]);

  // Refresh signed-in state on every route change so the header reflects
  // login/logout immediately without a full reload. /api/student/me.php
  // returns 401 for anonymous visitors — that just resets the state.
  useEffect(() => {
    let cancelled = false;
    api.studentMe()
      .then((data) => { if (!cancelled) setStudentMe(data); })
      .catch(() => { if (!cancelled) setStudentMe(null); });
    return () => { cancelled = true; };
  }, [location.pathname]);

  const handleStudentLogout = async () => {
    try { await api.studentLogout(); } catch (e) { /* ignore */ }
    setStudentMe(null);
    setStudentMenuOpen(false);
    navigate('/');
  };

  const studentFirstName = studentMe?.name?.split(' ')[0] || 'Account';

  const isSectionActive = (section) => {
    return section.path === location.pathname ||
      location.pathname.startsWith(section.path + '/') ||
      (section.items || []).some(i => !i.external && location.pathname === i.path.split('#')[0].split('?')[0]);
  };

  const renderMenuLink = (item) => {
    if (item.external) {
      return (
        <a
          key={item.label}
          href={item.path}
          target="_blank"
          rel="noopener noreferrer"
          className="header__dropdown-item"
        >
          {item.label}
        </a>
      );
    }
    if (item.path.includes('#') || item.path.includes('?')) {
      return (
        <a key={item.label} href={item.path} className="header__dropdown-item">
          {item.label}
        </a>
      );
    }
    return (
      <Link key={item.label} to={item.path} className="header__dropdown-item">
        {item.label}
      </Link>
    );
  };

  return (
    <header className="header">
      <div className="header__inner">
        <Link to="/" className="header__logo" aria-label="Mind Spa — home">
          <img src="/logo.png" alt="" className="header__logo-img" width="1063" height="1152" decoding="async" />
          <span className="header__logo-brand">Mind Spa</span>
        </Link>

        <nav className={`header__nav ${isMobileMenuOpen ? 'header__nav--open' : ''}`} aria-label="Primary">
          <button
            type="button"
            className="header__nav-close"
            aria-label="Close menu"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </button>
          <Link to="/" className={`header__nav-link ${location.pathname === '/' ? 'active' : ''}`}>
            Home
          </Link>

          {NAV.map((section) => {
            if (!section.items || section.items.length === 0) {
              return (
                <Link
                  key={section.label}
                  to={section.path}
                  className={`header__nav-link ${isSectionActive(section) ? 'active' : ''}`}
                >
                  {section.label}
                </Link>
              );
            }
            return (
              <div key={section.label} className="header__nav-dropdown">
                <button
                  type="button"
                  className={`header__nav-link header__nav-link--dropdown ${isSectionActive(section) ? 'active' : ''}`}
                  onClick={() => {
                    // Match the CSS off-canvas breakpoint (1280px) where the nav
                    // becomes the slide-in menu — below it, a dropdown tap opens its
                    // submenu rather than navigating.
                    if (window.innerWidth <= 1280) {
                      setOpenMobileDropdown(
                        openMobileDropdown === section.label ? null : section.label
                      );
                    } else {
                      navigate(section.path);
                    }
                  }}
                >
                  {section.label}
                  <Chevron />
                </button>
                <div
                  className={`header__dropdown-menu ${
                    openMobileDropdown === section.label ? 'header__dropdown-menu--open' : ''
                  }`}
                >
                  {section.items.map(renderMenuLink)}
                </div>
              </div>
            );
          })}

          <Link
            to="/courses"
            className={`header__nav-link ${location.pathname.startsWith('/courses') ? 'active' : ''}`}
          >
            Courses
          </Link>
        </nav>

        {isMobileMenuOpen && (
          <div
            className="header__backdrop"
            aria-hidden="true"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        {/* Quiet booking link — text only, with arrow */}
        <Link to="/contact" className="header__book-link">
          Book a session
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="5" y1="12" x2="19" y2="12"/>
            <polyline points="12 5 19 12 12 19"/>
          </svg>
        </Link>

        {/* Student auth chip — login link when signed out, name + dropdown when signed in. */}
        {studentMe ? (
          <div className="header__student" ref={studentMenuRef}>
            <button
              type="button"
              className="header__student-toggle"
              onClick={() => setStudentMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={studentMenuOpen}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              {studentFirstName}
              <Chevron />
            </button>
            {studentMenuOpen && (
              <div className="header__student-menu" role="menu">
                <Link to="/courses" className="header__dropdown-item" role="menuitem" onClick={() => setStudentMenuOpen(false)}>
                  My courses
                </Link>
                <button
                  type="button"
                  className="header__dropdown-item header__dropdown-item--button"
                  role="menuitem"
                  onClick={handleStudentLogout}
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        ) : (
          <Link to="/student/login" className="header__student-link">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            Student login
          </Link>
        )}

        <button
          className={`header__hamburger ${isMobileMenuOpen ? 'header__hamburger--active' : ''}`}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </header>
  );
};

export default Header;
